import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { PrintButton } from "@/Common/Components/Buttons/PrintButton";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import MedicalNotesConfiguration from "@/Patient/Components/MedicalNotesConfiguration/MedicalNotesConfiguration";
import { MedicalSummaryConfiguration } from "@/Patient/Components/MedicalSummaryConfiguration/MedicalSummaryConfiguration";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";

import styles from "./EncountersFilterForDirectEdits.module.scss";

import CreateDocument from "@/Globals/_images/create-document.svg";

interface IEncountersFilterForDirectEditsProps {
    model: IPatientMedicalSummaryModel;
    isReadOnly?: boolean;
    displayPrintButton?: boolean;
    fromMyleOperation?: number;
    toMyleOperation?: number;
    encounterSearchTermChangedOperation?: number;
    isPatientNoteDisabled?: boolean;
    id?: string;
    fromChange?: (date: Date) => void;
    toChange?: (date: Date) => void;
    onMyNotesFilterChanged?: (checked: boolean) => void;
    onClinicVisitNotesFilterChanged?: (checked: boolean) => void;
    newNote: (e: React.MouseEvent) => void;
    onClickCallbackPrintNotes?: () => void;
    onEncounterSearchTermChanged?: (textOrDate: any) => void;
    onSummaryConfigurationChange?: () => void;
    onNotesConfigurationChange?: () => void;
}

export function EncountersFilterForDirectEdits(props: IEncountersFilterForDirectEditsProps): JSX.Element {
    const { getText } = useTranslation();

    const isDisplayOnlyClinicVisitNotes = props.model.get("IsDisplayOnlyClinicVisitNotes");
    const encounterFilterMyNotes = props.model.get("EncounterFilterMyNotes");

    const showPrintBtn = !props.isReadOnly && props.displayPrintButton;

    return (
        <>
            <div className="flex-container">
                {!props.isReadOnly && (
                    <>
                        <MedicalNotesConfiguration
                            onConfigurationChange={(dateFrom, dateTo) => {
                                props.onNotesConfigurationChange?.();
                                props.fromChange(dateFrom);
                                props.toChange(dateTo);
                            }}
                        />
                        {cacheStore.ClinicSettings.EnableSummaryConfiguration && (
                            <MedicalSummaryConfiguration
                                onConfigurationChange={() => props.onSummaryConfigurationChange?.()}
                            />
                        )}
                    </>
                )}
            </div>
            <div style={props.isReadOnly && { padding: "10px 0" }}>
                <div className={styles.controlButtons}>
                    <div className="search-field width-100 margin-xs-right">
                        <div className="gf">
                            <span className="glyphicon glyphicon-search" />
                        </div>
                        <TextboxControl
                            dataCy="medicalNote-search"
                            debounceMs={400}
                            value={props.model.get("EncounterSearchTerm")}
                            className="fullWidth"
                            operation={props.encounterSearchTermChangedOperation}
                            onChange={(textOrDate: any) => {
                                if (props.onEncounterSearchTermChanged) {
                                    props.onEncounterSearchTermChanged(textOrDate);
                                }
                            }}
                            placeholder={getText("SearchLabel")}
                        />
                    </div>
                    {showPrintBtn && (
                        <PrintButton
                            className={`button control push-right-5 ${styles.printButton}`}
                            rightMargin={0}
                            onClickCallback={() => props.onClickCallbackPrintNotes?.()}
                        />
                    )}
                    {!props.isPatientNoteDisabled && (
                        <div title={getText("AddNewNoteLabel")}>
                            <button
                                data-cy="create-new-note"
                                className={`push-right-5 ${styles.createNewNote}`}
                                onClick={e => props.newNote(e)}
                            >
                                <div className={styles.createNewNoteIcon}>
                                    <CreateDocument />
                                </div>
                            </button>
                        </div>
                    )}
                </div>
                {!props.isReadOnly && (
                    <div className={styles.filterContainer}>
                        <ButtonControl
                            dataCy="medicalNote-search-filter-showMyNotes"
                            className={`btn btn-default ${styles.buttonFilterPositionLeft} ${!encounterFilterMyNotes ? "" : styles.filterSelected}`}
                            onClick={() => props.onMyNotesFilterChanged?.(!encounterFilterMyNotes)}
                            label={getText("ConfigureNotes_MedicalNoteFilterLabel")}
                        />

                        <ButtonControl
                            dataCy="medicalNote-search-filter-showClinicVisitNotes"
                            className={`btn btn-default ${styles.buttonFilterPositionLeft} ${!isDisplayOnlyClinicVisitNotes ? "" : styles.filterSelected}`}
                            onClick={() => props.onClinicVisitNotesFilterChanged?.(!isDisplayOnlyClinicVisitNotes)}
                            label={getText("ClinicVisitsLabel")}
                        />
                    </div>
                )}
                {props.model.get("IsSearchCriteriaExpanded") && (
                    <div className="margin-sm-top">
                        <MultiDateSelector
                            id={"doc_search_date_selector" + (props.id ?? "")}
                            dataCy="medicalNote-search-dates"
                            fullWidth={true}
                            fromMyleOperation={props.fromMyleOperation}
                            toMyleOperation={props.toMyleOperation}
                            fromChange={props.fromChange ? date => props.fromChange(date) : null}
                            toChange={props.toChange ? date => props.toChange(date) : null}
                            fromSetTodayLabel={getText("TodayLabel")}
                            toSetTodayLabel={getText("TodayLabel")}
                            from={props.model.get("EncounterSearchDateFrom")}
                            to={props.model.get("EncounterSearchDateTo")}
                        />
                    </div>
                )}
            </div>
        </>
    );
}
